import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Config } from '../app/constants/config';
import { type MatchMode, type CycleAction, ALL_CYCLE_ACTIONS, getNextTransition, findNextEnabledInCycle, buildEnabledActionsSet, isTransitionSkipped, normalizeEnabledActions } from '../utils/matchCycle';
import {
  type TeamSide,
  initialActiveTeam,
  advanceActiveTeam,
  deriveActiveTeam,
  isTraversingAction,
  isDirectReturn,
  nextDualTransition,
  flipSide,
} from '../utils/dualTeamCycle';
import { clampSetsToWinMatch, isMatchOver, isSetOver, type SetsToWinMatch } from '../utils/matchSetsToWin';
import {
  getDefaultAttackPassType,
  getRotationLabelFromCourtRoles,
  shouldApplyP1SideOutAttackSwap,
} from '../utils/attackPassTypes';
import { computeVisualCourt, computeSuggestedPlayers } from '../utils/courtVisual';
import {
  type InputSurface,
  type CaptureOptions,
  DEFAULT_CAPTURE_OPTIONS,
  normalizeCaptureOptions,
} from '../utils/keyboardInput/types';
import { durableStorage, persistAfterRecovery } from '../lib/durableStorage';
import { DEFAULT_ROSTER, DEFAULT_TEAM_TACTICS, EMPTY_TEAM } from './game/constants';
import { liveTacticsFor, p1SwapFor } from './game/teamRules';
import {
  mustDesignateBlocker,
  lastNonPhantomEvent,
  isAttackBlockedBackToAttacker,
  toDualRallySteps,
} from '../utils/visibleMatchActions';
import {
  isTerminalEvent,
  isPhantomEvent,
  isSameRally,
  nextRealEvent,
  computePointWinnerFromEvent,
  eventAuthorSide,
  SLASH_TERMINAL_ACTIONS,
} from './game/rallyPredicates';
import { migrateGameState } from './game/migration';
import { notifyPointConfirmed } from './game/pointConfirmed';
import { LIBERO_OFF_PREFIX, liberoTakenOffName } from '../utils/liberoChange';
import {
  autoLiberoSwap, liberoForcedInSet, liberosReceptionFirst, rosterLiberoPair, type LiberoPair,
} from '../utils/liberoPair';
import { readCaptureTimecodeMs } from './captureClock';
import { playingRoles, promoteRemainingSetter, type GameSystem } from '../utils/gameSystem';
import { POSITION_FAULT_ACTION, TEAM_FAULT_ACTIONS, type TeamFaultAction } from '../domain-ui/action/teamFaults';
import { getTargetStatus } from '../components/match-widgets/expert-court/constants';

/**
 * Timecode vidéo à figer sur un événement en cours de création (VS-16 lot 1).
 * Un seul point d'appel par site de création : en oublier un creuse un trou dans
 * la chronologie et rend le match « partiellement natif » côté serveur.
 */
const stampVideoTimecodeMs = (): number | undefined => readCaptureTimecodeMs();

/**
 * Moment where the scorer tapped the player (VOL-186). Scorers tap the player on
 * ball contact and the quality only once they see where the ball goes, so stamping
 * at commit lagged every action by seconds — on the video timecode and on `time`,
 * from which VS-15 derives timecodes when no capture video is mounted.
 *
 * Kept outside the persisted state on purpose: a reload between the tap and the
 * commit falls back to the commit moment. `pointEvents` and `history` are the
 * arrays at tap time; any event added or undone since (substitution, time-out,
 * other team's touch) changes their identity and voids the stamp, which also
 * guarantees `time` stays monotonic (the server orders by it).
 */
interface PlayerSelectionStamp {
  player: string;
  time: Date;
  videoTimecodeMs: number | undefined;
  /** Moment given by the caller (`at`): kept as is, even without a timecode. */
  given: boolean;
  pointEvents: GameEvent[];
  history: GameEvent[];
}

/** Moment to reuse instead of "now", e.g. the original moment of an action reloaded for correction. */
export interface ActionMoment {
  time: Date;
  videoTimecodeMs: number | undefined;
}

let playerSelectionStamp: PlayerSelectionStamp | null = null;

function takeEventStamp(
  player: string | null,
  pointEvents: GameEvent[],
  history: GameEvent[],
): ActionMoment {
  const stamp = playerSelectionStamp;
  playerSelectionStamp = null;
  if (
    stamp && player !== null && stamp.player === player &&
    stamp.pointEvents === pointEvents && stamp.history === history
  ) {
    // A tap made before the video was ready falls back to the commit position; a
    // given moment must not, or a corrected action entered without video would get
    // a timecode that does not match its time.
    const videoTimecodeMs = stamp.given
      ? stamp.videoTimecodeMs
      : (stamp.videoTimecodeMs ?? stampVideoTimecodeMs());
    return { time: stamp.time, videoTimecodeMs };
  }
  return { time: new Date(), videoTimecodeMs: stampVideoTimecodeMs() };
}

/**
 * Team at fault for `commitTeamFault`: the side given or the active team (two
 * teams), else the team being captured. Shared with the position-fault flow,
 * which must drop the serve of that same team.
 */
export function teamFaultSide(
  state: { dualTeamCapture: boolean; activeTeam: TeamSide; statTarget: 'us' | 'them' },
  side?: TeamSide,
): 'us' | 'them' {
  if (!state.dualTeamCapture && !side) return state.statTarget;
  return (side ?? state.activeTeam) === 'home' ? 'us' : 'them';
}

/**
 * Serve dated this long before its validation (VOL-200): the app preselects the
 * server, so there is no tap on contact, and a tap on the way to the line came
 * 5 to 8 s before the hit.
 */
export const SERVE_BEFORE_VALIDATION_MS = 2_000;
/** Pass of the setter the app preselected: dated this long after the reception (VOL-200). */
export const PASS_AFTER_RECEPTION_MS = 1_000;

function lastEventTime(pointEvents: GameEvent[], history: GameEvent[]): number {
  const last = pointEvents.at(-1) ?? history.at(-1);
  return last ? new Date(last.time).getTime() : -Infinity;
}

/**
 * Moment of a rally action. Over `takeEventStamp`, the two actions the app
 * preselects the player for: the serve, and the pass right after a reception
 * when the setter was not tapped again. Times never go before the previous
 * event: the server orders by them.
 */
function takeActionStamp(
  action: string | null,
  player: string | null,
  pointEvents: GameEvent[],
  history: GameEvent[],
): ActionMoment {
  const stamp = playerSelectionStamp;
  const now = Date.now();
  const nowTimecode = stampVideoTimecodeMs();
  if (action === 'Service' && !stamp?.given) {
    playerSelectionStamp = null;
    return {
      time: new Date(Math.max(now - SERVE_BEFORE_VALIDATION_MS, lastEventTime(pointEvents, history) + 1)),
      videoTimecodeMs: nowTimecode === undefined ? undefined : Math.max(0, nowTimecode - SERVE_BEFORE_VALIDATION_MS),
    };
  }
  const tapped = stamp !== null && stamp.player === player
    && stamp.pointEvents === pointEvents && stamp.history === history;
  const reception = pointEvents.at(-1);
  if (action === 'Passe' && !tapped && reception?.action === 'Réception') {
    playerSelectionStamp = null;
    const receivedAt = new Date(reception.time).getTime();
    const passTimecode = reception.videoTimecodeMs === undefined || nowTimecode === undefined
      ? nowTimecode
      : Math.min(reception.videoTimecodeMs + PASS_AFTER_RECEPTION_MS, nowTimecode);
    return {
      time: new Date(Math.min(receivedAt + PASS_AFTER_RECEPTION_MS, Math.max(now, receivedAt + 1))),
      videoTimecodeMs: passTimecode,
    };
  }
  return takeEventStamp(player, pointEvents, history);
}

export type PlayerRole = 'Passeur' | 'R4' | 'Pointu' | 'Central' | 'Lib';
export type ActionType = 'Service' | 'Réception' | 'Passe' | 'Attaque' | 'Bloc' | 'Défense' | 'Freeball Donnée' | 'Freeball Reçue' | 'Retour Direct' | 'Faute Adversaire' | 'Point Adversaire' | 'Faute de Fil' | 'Mordu' | 'Double Touches' | 'Porté' | 'Quatre Touches' | 'Faute de Position' | 'Substitution' | 'Temps Mort' | 'Temps Mort Adversaire' | 'SET_START' | 'LIBERO_CHANGE' | 'MATCH_END';

export { TEAM_FAULT_ACTIONS, type TeamFaultAction };
export type ActionQuality = '#' | '+' | '!' | '-' | '/' | '=' | '0';
export type GamePhase = 'Serve' | 'Reception' | 'Set' | 'Attack' | 'Block' | 'Dig' | 'PointEnd' | 'SetEnd' | 'SetSetup' | 'MatchEnd';

/**
 * Quality stamped on a `Bloc` designated in one tap (two-team capture), read from
 * where the attack target says the ball went (DataVolley codes, VOL-189): sent
 * back to the attacker → "!" (covered); stayed on the blocker's side → "+".
 * A kill block is entered on the attack ("/"), a block fault on the point.
 */
function blockerDesignationQuality(attack: GameEvent | undefined): ActionQuality {
  return isAttackBlockedBackToAttacker(attack) ? '!' : '+';
}

export type { MatchMode, CycleAction };
export type { SetsToWinMatch };
export type { InputSurface, CaptureOptions };

export interface TeamTactics {
  p1AttackZone2: 'R4' | 'Pointu';
  liberoDefenseZone: 5 | 6;
  centralDefenseZone: 5 | 6;
  /** Roster ids of the players the libero replaces (at most 2); empty = back-row `Central`. */
  liberoReplaces?: string[];
  /** Game system (VOL-162); absent = classic 5-1. */
  gameSystem?: GameSystem;
  /** Roster ids of the reception / defense liberos, pre-filled at match setup (VOL-163). */
  liberoPair?: LiberoPair;
}

export interface Player {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role: PlayerRole;
  number?: number;
  /** Reception or defense libero of a two-libero team (VOL-163), declared before the match. */
  liberoRole?: keyof LiberoPair;
}

/** Métadonnées de session pour rouvrir l’écran composition (équipe) après reload. */
export interface MatchSessionMeta {
  opponent: string;
  date: string;
  location: string;
  championship?: string;
  teamId?: string;
  awayOpponentTeamId?: string;
}

export interface TeamState {
  roster: Player[];
  court: string[];
  courtRoles: PlayerRole[];
  bench: string[];
  libero: string | null;
  /** Reception / defense liberos of the current set (VOL-163); absent = one libero. */
  liberos?: LiberoPair | null;
  playerRoles: Record<string, PlayerRole>;
}

export interface GameEvent {
  id: string;
  set: number;
  scoreUs: number;
  scoreThem: number;
  player: string;
  role: string;
  action: ActionType | string;
  quality: ActionQuality;
  zone?: string;
  rotation?: string; // P1..P6
  courtPosition?: number;
  phase?: 'K1' | 'K2'; // K1 = Side-Out (adversaire sert), K2 = Break-Point (nous servons)
  middleBlockerCall?: string;
  passType?: string;
  serviceType?: string;
  isReversal?: boolean;
  playerOutId?: string;
  targetX?: number;
  targetY?: number;
  originX?: number;
  originY?: number;
  touchedByBlock?: boolean;
  /** Nombre de bloqueurs en face de l'attaque (0 à 3). Absent = non renseigné. */
  blockersCount?: number;
  isOpponent?: boolean;
  // Convergence VS-12 (Lot 0) : `home` = notre équipe, `away` = adversaire.
  // Optionnel tant que l'`activeTeam` (Lot 1) n'est pas branché : par défaut
  // dérivé de `isOpponent` à la sérialisation (voir `gameEventToDto`).
  teamSide?: 'home' | 'away';
  /**
   * Position absolue du lecteur vidéo au moment de la validation (VS-16 lot 1).
   * Figé ici, jamais relu au flush. Absent hors saisie vidéo : le serveur
   * complète alors par dérivation depuis l'heure de saisie (VS-15).
   */
  videoTimecodeMs?: number;
  time: Date;
}

interface SetInfo {
  startingLineup: string[]; // Noms ou IDs
  timeOutsUs: number;
  timeOutsThem: number;
  firstServer?: 'us' | 'them'; // Optional, inferred from first action
}

// Forme historique (pré-migration) d'un état sauvegardé : avant la refonte
// `teams: { us, them }`, les champs étaient à plat sur le state.
// On conserve cette forme ici pour pouvoir restaurer d'anciennes sauvegardes
// sans recourir à des casts à chaque accès.
interface LegacySavedGameState {
  roster?: Player[];
  court?: string[];
  courtRoles?: PlayerRole[];
  bench?: string[];
  libero?: string | null;
  playerRoles?: Record<string, PlayerRole>;
  opponentRoster?: Player[];
  opponentCourt?: string[];
  opponentCourtRoles?: PlayerRole[];
  opponentBench?: string[];
  opponentLibero?: string | null;
  liberos?: LiberoPair | null;
  opponentLiberos?: LiberoPair | null;
}

/** Entrée de roster adverse par numéro (VS-12 — Lot 3). Nom/rôle optionnels. */
export interface OpponentNumberEntry {
  number: number;
  name?: string;
  role?: PlayerRole;
}

export interface GameState {
  teams: { us: TeamState; them: TeamState };
  statTarget: 'us' | 'them';

  currentSet: number;
  scoreUs: number;
  scoreThem: number;
  setScores: Record<number, { us: number, them: number }>;
  setInfos: Record<number, SetInfo>;

  servingSide: 'us' | 'them';

  /**
   * Mode « deux équipes » (VS-12 — Lot 1). Quand `true`, chaque action du rallye
   * est attribuée à l'équipe active (`activeTeam`), qui bascule sur les actions
   * traversantes. Défaut `false` = mode une équipe historique (inchangé).
   */
  dualTeamCapture: boolean;
  /** Équipe active en saisie deux équipes (`home` = notre équipe, `away` = adversaire). */
  activeTeam: TeamSide;

  timeOutsUs: number;
  timeOutsThem: number;

  gamePhase: GamePhase;
  lastTouchPlayer: string | null;

  selectedPlayer: string | null;
  selectedAction: ActionType;
  selectedMiddleBlockerCall: string | null;
  selectedPassType: string | null;
  selectedServiceType: string | null;
  selectedIsReversal: boolean;
  /**
   * Nombre de bloqueurs en face de l'attaque en cours de saisie (0 à 3), `null`
   * tant qu'il n'est pas renseigné — l'information reste facultative, un coach
   * qui ne la touche pas ne doit pas voir « 0 bloqueur » enregistré à sa place.
   */
  selectedBlockersCount: number | null;
  pointWinner: 'us' | 'them' | null;

  teamName: string;
  opponentName: string;
  matchId: string | null;
  isP1SwapActive: boolean;
  teamTactics: TeamTactics;
  /** Configuration tactique de l'équipe B (saisie deux équipes). */
  opponentTeamTactics: TeamTactics;

  matchMode: MatchMode;
  enabledActions: CycleAction[];
  simplifiedQualities: boolean;
  setsToWinMatch: SetsToWinMatch;

  inputSurface: InputSurface;
  captureOptions: CaptureOptions;

  history: GameEvent[];
  currentPointEvents: GameEvent[];

  /** Alignement titulaires validé via l’écran composition (ou restauration serveur). */
  ownStartingLineupValidated: boolean;
  /** Équipe active pour renvoyer vers `/team/:id` si la composition n’est pas validée. */
  matchSessionTeamId: string | null;
  matchSessionMeta: MatchSessionMeta | null;

  setTeamName: (name: string) => void;
  setOpponentName: (name: string) => void;
  /**
   * Configuration tactique. `side` omis = notre équipe (comportement historique) ;
   * en deux équipes, l'équipe B a la sienne (`opponentTeamTactics`), éditable au
   * même titre que la nôtre.
   */
  setTeamTactics: (tactics: Partial<TeamTactics>, side?: 'us' | 'them') => void;
  setStatTarget: (target: 'us' | 'them') => void;
  /**
   * Désigne l'équipe au service **en début de set**. L'engagement du premier point
   * ne se déduit d'aucune action précédente : c'est un choix de l'utilisateur (en
   * une équipe il est implicite dans le choix Service/Réception, en deux équipes les
   * deux camps commencent par un service). Ignoré dès qu'un point est entamé ou
   * marqué, pour ne pas désynchroniser rotations et équipe active.
   */
  setServingSide: (side: 'us' | 'them') => void;
  setOpponentRoster: (players: Player[]) => void;
  /**
   * Roster équipe B **par numéros** (VS-12 — Lot 3) : identités optionnelles.
   * Chaque entrée = un numéro de maillot ; nom/rôle facultatifs (défaut : `#N` / `R4`).
   */
  setOpponentRosterByNumbers: (entries: OpponentNumberEntry[]) => void;
  setOpponentStartingCourt: (court: string[], courtRoles: PlayerRole[], bench: string[]) => void;
  setMatchId: (id: string | null) => void;
  setMatchMode: (mode: MatchMode) => void;
  setDualTeamCapture: (enabled: boolean) => void;
  setEnabledActions: (actions: CycleAction[]) => void;
  setSimplifiedQualities: (simplified: boolean) => void;
  setSetsToWinMatch: (value: number) => void;
  setInputSurface: (surface: InputSurface) => void;
  setCaptureOptions: (options: Partial<CaptureOptions>) => void;
  /**
   * Enregistre un temps mort et renvoie l'identifiant de l'événement créé —
   * c'est lui qui permet d'annuler un temps mort demandé par erreur.
   */
  addTimeOut: (team: 'us' | 'them') => string;
  /**
   * Retire un temps mort de l'historique et rend le crédit à l'équipe.
   * Sans effet si l'identifiant ne correspond pas à un temps mort.
   */
  cancelTimeOut: (eventId: string) => void;
  loadMatchState: (savedState: Partial<GameState> & LegacySavedGameState) => void;
  setRoster: (players: Player[]) => void;
  /** Adds a player who arrived late to our roster and bench, mid-match (VOL-208). */
  addLatePlayer: (player: Player) => void;
  /**
   * Gestes manuels d'effectif. `side` omis = équipe en saisie (`_captureSide`,
   * comportement historique) ; en deux équipes on peut viser explicitement l'une ou
   * l'autre équipe, sans attendre qu'elle ait la main.
   */
  substitutePlayer: (playerOut: string, playerIn: string, side?: 'us' | 'them') => void;
  /** Returns the id of the `LIBERO_CHANGE` event, undefined when nothing changed. */
  setLibero: (name: string | null, side?: 'us' | 'them') => string | undefined;
  /** Undoes a take-off locally (event removed, libero back in); the caller undoes it server-side. */
  cancelLiberoTakeOff: (eventId: string, side?: 'us' | 'them') => void;
  rotate: (side?: 'us' | 'them') => void;

  /**
   * `auto`: selection made by the app (server pre-selected), not a tap on ball
   * contact — it must not stamp the action's time.
   * `at`: moment to keep for the action instead of the tap (action reloaded for
   * correction keeps its original time and video timecode).
   */
  selectPlayer: (name: string, options?: { auto?: boolean; at?: ActionMoment }) => void;
  selectAction: (action: ActionType) => void;
  selectMiddleBlockerCall: (call: string | null) => void;
  selectPassType: (type: string | null) => void;
  selectServiceType: (type: string | null) => void;
  selectBlockersCount: (count: number | null) => void;
  selectIsReversal: (val: boolean) => void;
  commitAction: (quality: ActionQuality, zone?: string, target?: { x: number; y: number; touched?: boolean }, origin?: { x: number; y: number }) => void;
  /**
   * For a typed team fault (`TEAM_FAULT_ACTIONS`), the faulting side is derived
   * from `winner`: callers pass the opponent of the player's team as winner.
   */
  commitOpponentAction: (winner: 'us' | 'them', reason: string, player?: string) => void;
  /**
   * Faute terminale attribuée à l'équipe active (deux équipes) ou à l'équipe
   * courante (une équipe) → point à l'équipe adverse. Reproduit la sémantique de
   * `commitOpponentAction` (vainqueur + type de faute) via le pipeline live A/B.
   */
  /**
   * `side` overrides the active team: a position fault can be called on the
   * receiving team before any touch, while the active team is still the server.
   */
  commitTeamFault: (faultType: TeamFaultAction, player?: string, side?: TeamSide) => void;

  updateEvent: (id: string, updates: Partial<GameEvent>) => void;
  /**
   * Gives new ids to events of the rally in progress whose server copy was undone,
   * so the sync queue sends them again (edit at point end, VOL-221).
   */
  rekeyEvents: (ids: string[]) => Record<string, string>;
  deleteEvent: (id: string) => void;

  confirmPoint: () => void;
  cancelPointEnd: () => void;
  nextSet: () => void;
  designateSetter: (courtIndex: number, side?: 'us' | 'them') => void;
  /**
   * Poste tenu par le joueur d'une position du terrain, en cours de match.
   *
   * Même portée que le sélecteur de la compo de début de set : c'est le rôle
   * **tactique** de la position (`courtRoles`) qui change — celui qui sert aux
   * stats et à la rotation — pas le poste du joueur dans l'effectif.
   */
  setCourtRole: (courtIndex: number, role: PlayerRole, side?: 'us' | 'them') => void;
  /**
   * Compo de départ d'une équipe pour un nouveau set (VS-12 — deux équipes).
   */
  startSet: (
    court: string[],
    courtRoles: PlayerRole[],
    bench: string[],
    awayLineup?: { court: string[]; courtRoles: PlayerRole[]; bench: string[]; liberos?: LiberoPair | null },
    liberos?: LiberoPair | null,
  ) => void;
  skipToScore: (set: number, scoreUs: number, scoreThem: number, servingSide: 'us' | 'them', pastSetScores?: Record<number, { us: number; them: number }>) => void;

  modifyLastPoint: () => void;
  rollbackToScore: (targetScoreUs: number, targetScoreThem: number) => GameEvent[];
  undo: () => void;

  /**
   * Vue terrain (permutations, libéro) d'un côté. `side` omis = équipe scoutée
   * (`statTarget`, comportement une équipe inchangé) ; en saisie deux équipes,
   * chaque moitié demande explicitement la sienne.
   */
  getVisualCourt: (side?: 'us' | 'them') => string[];
  getActivePlayerRoles: () => Record<string, PlayerRole>;
  getActiveLibero: () => string | null;
  /** Joueurs suggérés d'un côté (même convention de `side` que `getVisualCourt`). */
  getSuggestedPlayers: (side?: 'us' | 'them') => string[];
  resetMatch: () => void;
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => {

      // ─── Internal helpers ──────────────────────────────────────────────
      const _team = (side: 'us' | 'them'): TeamState => get().teams[side];
      const _active = (): TeamState => _team(get().statTarget);

      /**
       * Côté d'équipe visé par les actions manuelles hors rallye (remplacement,
       * rotation, libéro, désignation passeur). En mode deux équipes (VS-12), ces
       * gestes doivent cibler l'**équipe active** (`activeTeam` : home→us, away→them)
       * pour pouvoir gérer l'adversaire ; en mode une équipe on garde `statTarget`
       * (comportement historique strictement inchangé).
       */
      const _captureSide = (): 'us' | 'them' => {
        const s = get();
        if (!s.dualTeamCapture) return s.statTarget;
        return s.activeTeam === 'home' ? 'us' : 'them';
      };

      /** Défaut sûr pour un état persisté antérieur à `opponentTeamTactics`. */
      const _tactics = (value: TeamTactics | undefined): TeamTactics =>
        value ?? DEFAULT_TEAM_TACTICS;

      /**
       * Configuration tactique du côté visé (B a la sienne en deux équipes), avec
       * les remplaçants qui héritent de « Le libéro remplace » dans le set.
       */
      const _tacticsFor = (side: 'us' | 'them'): TeamTactics => {
        const s = get();
        return liveTacticsFor({ ...s, opponentTeamTactics: _tactics(s.opponentTeamTactics) }, side);
      };

      const _setTeam = (side: 'us' | 'them', data: Partial<TeamState>) =>
        set(state => ({
          teams: { ...state.teams, [side]: { ...state.teams[side], ...data } }
        }));

      const _rotateTeam = (side: 'us' | 'them') => {
        const { court, courtRoles } = _team(side);
        if (court.length < 6) return;
        const positions = court.slice(0, 6);
        const roles = courtRoles.slice(0, 6);
        const libSlot = court.slice(6);
        const libRoles = courtRoles.slice(6);
        const p1 = positions.shift();
        if (p1) positions.push(p1);
        const r1 = roles.shift();
        if (r1) roles.push(r1);
        _setTeam(side, {
          court: [...positions, ...libSlot],
          courtRoles: [...roles, ...libRoles],
        });
      };

      const _unrotateTeam = (side: 'us' | 'them') => {
        const { court, courtRoles } = _team(side);
        if (court.length < 6) return;
        const positions = court.slice(0, 6);
        const roles = courtRoles.slice(0, 6);
        const libSlot = court.slice(6);
        const libRoles = courtRoles.slice(6);
        const last = positions.pop();
        if (last) positions.unshift(last);
        const lastR = roles.pop();
        if (lastR) roles.unshift(lastR);
        _setTeam(side, {
          court: [...positions, ...libSlot],
          courtRoles: [...roles, ...libRoles],
        });
      };

      /**
       * Type de frappe proposé par défaut à l'attaque. Suit le **côté en saisie**
       * (`_captureSide`) : en deux équipes, l'attaquant adverse reçoit son type par
       * poste (X5/X6/Xp…) comme le nôtre, au lieu de retomber sur « Libre » faute
       * de trouver le joueur dans notre effectif.
       */
      const _defaultPassForAttack = (player: string | null, action: ActionType): string | null => {
        if (action !== 'Attaque' || !player) return null;
        const side = _captureSide();
        const t = _team(side);
        const s = get();
        // Drapeau P1 et tactique de l'équipe SAISIE, dans les deux calculs : le
        // terrain prenait déjà la bonne tactique, mais le type de frappe
        // retombait sur la nôtre.
        const p1Swap = p1SwapFor(s, side);
        const tactics = _tacticsFor(side);
        const visualCourt = computeVisualCourt({
          team: t,
          statTarget: side,
          servingSide: s.servingSide,
          gamePhase: s.gamePhase,
          isP1SwapActive: p1Swap,
          teamTactics: tactics,
        });
        return getDefaultAttackPassType({
          statTarget: side,
          selectedPlayer: player,
          court: t.court,
          courtRoles: t.courtRoles,
          playerRoles: t.playerRoles,
          isP1SwapActive: p1Swap,
          teamTactics: tactics,
          visualCourt,
        });
      };

      // ─── State + Actions ───────────────────────────────────────────────
      return {
      teams: {
        us: { roster: DEFAULT_ROSTER, court: [], courtRoles: [], bench: [], libero: null, playerRoles: {} },
        them: { ...EMPTY_TEAM },
      },
      statTarget: 'us' as const,

      currentSet: 1,
      scoreUs: 0,
      scoreThem: 0,
      setScores: {},
      setInfos: {},
      servingSide: 'us',

      dualTeamCapture: false,
      activeTeam: 'home' as TeamSide,

      timeOutsUs: 0,
      timeOutsThem: 0,

      gamePhase: 'Serve',
      lastTouchPlayer: null,

      selectedPlayer: null,
      selectedAction: 'Service',
      selectedMiddleBlockerCall: null,
      selectedPassType: null,
      selectedServiceType: null,
      selectedIsReversal: false,
      selectedBlockersCount: null,
      pointWinner: null,

      teamName: '',
      opponentName: '',
      matchId: null,
      isP1SwapActive: false,
      teamTactics: { ...DEFAULT_TEAM_TACTICS },
      opponentTeamTactics: { ...DEFAULT_TEAM_TACTICS },

      matchMode: 'video',
      enabledActions: [...ALL_CYCLE_ACTIONS],
      simplifiedQualities: true,
      setsToWinMatch: clampSetsToWinMatch(Config.match.setsToWin),

      inputSurface: 'touch',
      captureOptions: { ...DEFAULT_CAPTURE_OPTIONS },

      history: [],
      currentPointEvents: [],

      ownStartingLineupValidated: false,
      matchSessionTeamId: null,
      matchSessionMeta: null,

      setTeamName: (name) => set({ teamName: name }),
      setOpponentName: (name) => set({ opponentName: name }),
      setTeamTactics: (tactics, side) => set((state) =>
        (side ?? 'us') === 'them'
          ? { opponentTeamTactics: { ..._tactics(state.opponentTeamTactics), ...tactics } }
          : { teamTactics: { ...state.teamTactics, ...tactics } },
      ),
      setStatTarget: (target) => set({ statTarget: target, selectedPlayer: null }),

      setServingSide: (side) => {
        const {
          scoreUs, scoreThem, currentPointEvents, servingSide,
          currentSet, setInfos, statTarget, dualTeamCapture,
        } = get();
        if (side === servingSide) return;
        const atSetStart = scoreUs === 0 && scoreThem === 0 && currentPointEvents.length === 0;
        if (!atSetStart) return;

        // Deux équipes : le rallye s'ouvre sur le service de l'équipe qui sert.
        // Une équipe : l'équipe scoutée sert ou réceptionne (comportement historique).
        const targetIsServing = dualTeamCapture || side === statTarget;
        const usRotation = getRotationLabelFromCourtRoles(_team('us').courtRoles);
        const info = setInfos[currentSet];

        set({
          servingSide: side,
          gamePhase: targetIsServing ? 'Serve' : 'Reception',
          selectedAction: targetIsServing ? 'Service' : 'Réception',
          selectedPlayer: null,
          selectedPassType: null,
          isP1SwapActive: shouldApplyP1SideOutAttackSwap(usRotation, side),
          ...(dualTeamCapture ? { activeTeam: initialActiveTeam(side) } : {}),
          ...(info
            ? { setInfos: { ...setInfos, [currentSet]: { ...info, firstServer: side } } }
            : {}),
        });
      },

      setOpponentRoster: (players) => {
        if (players.length === 0) {
          _setTeam('them', { ...EMPTY_TEAM });
          return;
        }
        const nonLib = players.filter(p => p.role !== 'Lib');
        const liberos = rosterLiberoPair(players);
        const libs = liberosReceptionFirst(players.filter(p => p.role === 'Lib'), liberos);
        const starters = nonLib.slice(0, 6);
        const oppCourt = starters.map(p => p.name);
        const oppCourtRoles: PlayerRole[] = starters.map(p => p.role);
        const lib = libs[0] ?? null;
        if (lib) {
          oppCourt.push(lib.name);
          oppCourtRoles.push('Lib');
        }
        const oppBench = [...nonLib.slice(6), ...libs.slice(1)].map(p => p.name);
        const oppPlayerRoles: Record<string, PlayerRole> = {};
        players.forEach(p => oppPlayerRoles[p.name] = p.role);
        _setTeam('them', {
          roster: players,
          court: oppCourt,
          courtRoles: oppCourtRoles,
          bench: oppBench,
          libero: lib ? lib.name : null,
          liberos,
          playerRoles: oppPlayerRoles,
        });
      },

      setOpponentRosterByNumbers: (entries) => {
        // Construit un roster adverse anonyme à partir des numéros. Nom par défaut
        // « #N », rôle par défaut « R4 » (aucune tactique adverse requise en live).
        // On déduplique/trie par numéro pour une compo stable.
        const seen = new Set<number>();
        const players: Player[] = entries
          .filter((e) => Number.isFinite(e.number) && !seen.has(e.number) && seen.add(e.number))
          .sort((a, b) => a.number - b.number)
          .map((e) => ({
            id: `opp-${e.number}`,
            name: e.name?.trim() ? e.name.trim() : `#${e.number}`,
            role: e.role ?? 'R4',
            number: e.number,
          }));
        get().setOpponentRoster(players);
      },

      setOpponentStartingCourt: (court, courtRoles, bench) =>
        _setTeam('them', { court, courtRoles, bench }),

      setMatchId: (id: string | null) => set({ matchId: id }),
      setMatchMode: (mode) => set({ matchMode: mode }),
      setDualTeamCapture: (enabled) => {
        // À l'activation, l'équipe active repart du serveur du rallye courant.
        set({
          dualTeamCapture: enabled,
          activeTeam: enabled
            ? deriveActiveTeam(get().servingSide, toDualRallySteps(get().currentPointEvents))
            : 'home',
        });
      },
      setEnabledActions: (actions) => {
        set({ enabledActions: normalizeEnabledActions(actions) });
      },
      setSimplifiedQualities: (simplified) => set({ simplifiedQualities: simplified }),
      setSetsToWinMatch: (value) => set({ setsToWinMatch: clampSetsToWinMatch(value) }),
      setInputSurface: (surface) => set({ inputSurface: surface }),
      setCaptureOptions: (options) =>
        set((state) => ({ captureOptions: { ...state.captureOptions, ...options } })),

      addTimeOut: (team) => {
          const { currentSet, scoreUs, scoreThem } = get();
          const timeOutEvent: GameEvent = {
              id: crypto.randomUUID(),
              time: new Date(),
              set: currentSet,
              scoreUs, scoreThem,
              player: team === 'us' ? 'Coach' : 'Coach Adversaire',
              role: 'Coach',
              action: team === 'us' ? 'Temps Mort' : 'Temps Mort Adversaire',
              quality: '0',
              zone: '',
              videoTimecodeMs: stampVideoTimecodeMs(),
          };
          set((state) => ({
              timeOutsUs: team === 'us' ? state.timeOutsUs + 1 : state.timeOutsUs,
              timeOutsThem: team === 'them' ? state.timeOutsThem + 1 : state.timeOutsThem,
              history: [...state.history, timeOutEvent]
          }));
          return timeOutEvent.id;
      },

      cancelTimeOut: (eventId) => {
          const event = get().history.find((e) => e.id === eventId);
          if (!event) return;
          const isUs = event.action === 'Temps Mort';
          const isThem = event.action === 'Temps Mort Adversaire';
          if (!isUs && !isThem) return;
          set((state) => ({
              timeOutsUs: isUs ? Math.max(0, state.timeOutsUs - 1) : state.timeOutsUs,
              timeOutsThem: isThem ? Math.max(0, state.timeOutsThem - 1) : state.timeOutsThem,
              history: state.history.filter((e) => e.id !== eventId),
          }));
      },

      loadMatchState: (savedState) => {
          // Migration: flat fields → teams
          let teams = savedState.teams;
          if (!teams) {
              const cur = get().teams;
              const oppRoster: Player[] = savedState.opponentRoster ?? cur.them.roster;
              teams = {
                us: {
                  roster: savedState.roster ?? cur.us.roster,
                  court: savedState.court ?? cur.us.court,
                  courtRoles: savedState.courtRoles ?? cur.us.courtRoles,
                  bench: savedState.bench ?? cur.us.bench,
                  libero: savedState.libero !== undefined ? savedState.libero : cur.us.libero,
                  // Kept unless the caller passes it: a score-only resync runs mid-set. A
                  // resume passes it (null at worst), never keeping another match's pair.
                  liberos: savedState.liberos !== undefined ? savedState.liberos : cur.us.liberos,
                  playerRoles: savedState.playerRoles ?? cur.us.playerRoles,
                },
                them: {
                  roster: oppRoster,
                  court: savedState.opponentCourt ?? cur.them.court,
                  courtRoles: savedState.opponentCourtRoles ?? cur.them.courtRoles,
                  bench: savedState.opponentBench ?? cur.them.bench,
                  libero: savedState.opponentLibero !== undefined ? savedState.opponentLibero : cur.them.libero,
                  liberos: savedState.opponentLiberos !== undefined ? savedState.opponentLiberos : cur.them.liberos,
                  playerRoles: oppRoster.length > 0
                    ? Object.fromEntries(oppRoster.map(p => [p.name, p.role]))
                    : cur.them.playerRoles,
                },
              };
          }

          // On type cleanPartial comme intersection (GameState & LegacySaved)
          // pour que `delete` accepte les clés legacy sans cast runtime.
          const cleanPartial: Partial<GameState & LegacySavedGameState> = { ...savedState };
          const legacyKeys: (keyof (GameState & LegacySavedGameState))[] = [
            'roster', 'court', 'courtRoles', 'bench', 'libero', 'playerRoles',
            'opponentRoster', 'opponentCourt', 'opponentCourtRoles', 'opponentBench',
            'opponentLibero', 'liberos', 'opponentLiberos', 'teams',
          ];
          for (const k of legacyKeys) {
            delete cleanPartial[k];
          }

          const computedP1Swap: boolean = savedState.isP1SwapActive !== undefined
            ? savedState.isP1SwapActive
            : shouldApplyP1SideOutAttackSwap(
                getRotationLabelFromCourtRoles(teams.us.courtRoles),
                savedState.servingSide ?? get().servingSide,
              );

          set((state) => ({
              ...state,
              ...cleanPartial,
              teams,
              isP1SwapActive: computedP1Swap,
          }));
      },

      resetMatch: () => {
          set({
              teams: {
                us: { roster: DEFAULT_ROSTER, court: [], courtRoles: [], bench: [], libero: null, playerRoles: {} },
                them: { ...EMPTY_TEAM },
              },
              currentSet: 1,
              scoreUs: 0,
              scoreThem: 0,
              setScores: {},
              setInfos: {},
              servingSide: 'us',
              dualTeamCapture: false,
              activeTeam: 'home',
              timeOutsUs: 0,
              timeOutsThem: 0,
              gamePhase: 'Serve',
              lastTouchPlayer: null,
              selectedPlayer: null,
              selectedAction: 'Service',
              selectedServiceType: null,
              selectedBlockersCount: null,
              pointWinner: null,
              history: [],
              currentPointEvents: [],
              teamName: '',
              opponentName: '',
              statTarget: 'us' as const,
              matchId: null,
              isP1SwapActive: false,
              teamTactics: { ...DEFAULT_TEAM_TACTICS },
              opponentTeamTactics: { ...DEFAULT_TEAM_TACTICS },
              matchMode: 'video',
              enabledActions: [...ALL_CYCLE_ACTIONS],
              simplifiedQualities: true,
              setsToWinMatch: clampSetsToWinMatch(Config.match.setsToWin),
              inputSurface: 'touch',
              captureOptions: { ...DEFAULT_CAPTURE_OPTIONS },
              ownStartingLineupValidated: false,
              matchSessionTeamId: null,
              matchSessionMeta: null,
          });
      },

      addLatePlayer: (player) => {
        const t = _team('us');
        if (t.roster.some((p) => p.id === player.id || p.name === player.name)) return;
        _setTeam('us', {
          roster: [...t.roster, player],
          bench: [...t.bench, player.name],
          playerRoles: { ...t.playerRoles, [player.name]: player.role },
        });
      },

      setRoster: (players) => {
        if (players.length === 0) return;
        const nonLib = players.filter(p => p.role !== 'Lib');
        const liberos = rosterLiberoPair(players);
        const libs = liberosReceptionFirst(players.filter(p => p.role === 'Lib'), liberos);
        const starters = nonLib.slice(0, 6);
        const court = starters.map(p => p.name);
        const courtRoles: PlayerRole[] = starters.map(p => p.role);
        const lib = libs[0] ?? null;
        if (lib) { court.push(lib.name); courtRoles.push('Lib'); }
        const bench = [...nonLib.slice(6), ...libs.slice(1)].map(p => p.name);
        const roles: Record<string, PlayerRole> = {};
        players.forEach(p => roles[p.name] = p.role);
        _setTeam('us', { roster: players, court, courtRoles, bench, playerRoles: roles, libero: lib ? lib.name : null, liberos });
      },

      substitutePlayer: (playerOut: string, playerIn: string, targetSide) => {
          const side = targetSide ?? _captureSide();
          const isOpp = side === 'them';
          const t = _team(side);
          const { currentSet, scoreUs, scoreThem, currentPointEvents } = get();

          const idxOut = t.court.indexOf(playerOut);
          if (idxOut === -1) return;

          const newCourt = [...t.court];
          newCourt[idxOut] = playerIn;
          const newBench = t.bench.filter(b => b !== playerIn);
          newBench.push(playerOut);

          const roleIn = t.playerRoles[playerIn];
          const roleOut = t.playerRoles[playerOut];
          const isDoubleSub = (roleIn === 'Passeur' && roleOut === 'Pointu') || (roleIn === 'Pointu' && roleOut === 'Passeur');
          const pOutObj = t.roster.find(p => p.name === playerOut);

          const newCourtRoles = [...t.courtRoles];
          if (isDoubleSub) {
              newCourtRoles[idxOut] = roleIn;
              const oppositeRole = roleIn === 'Passeur' ? 'Pointu' : 'Passeur';
              const duplicateIdx = newCourtRoles.findIndex((r, i) => i !== idxOut && r === roleIn);
              if (duplicateIdx !== -1) newCourtRoles[duplicateIdx] = oppositeRole;
          }
          promoteRemainingSetter(newCourt, newCourtRoles, t.playerRoles, t.courtRoles[idxOut] === 'Passeur');

          const subEvent: GameEvent = {
              id: crypto.randomUUID(), time: new Date(), set: currentSet, scoreUs, scoreThem,
              player: playerIn, role: 'Sub', action: 'Substitution', quality: '0',
              zone: `OUT:${playerOut}`, playerOutId: pOutObj?.id, isOpponent: isOpp || undefined,
              videoTimecodeMs: stampVideoTimecodeMs(),
          };

          _setTeam(side, { court: newCourt, courtRoles: newCourtRoles, bench: newBench });
          set({ currentPointEvents: [...currentPointEvents, subEvent] });
      },

      setLibero: (newLiberoName, targetSide) => {
          const side = targetSide ?? _captureSide();
          const isOpp = side === 'them';
          const t = _team(side);
          const { currentSet, scoreUs, scoreThem, currentPointEvents } = get();

          if (newLiberoName === t.libero) return;

          let newBench = [...t.bench];
          const newCourt = [...t.court];
          const newCourtRoles = [...t.courtRoles];

          if (newLiberoName) newBench = newBench.filter(p => p !== newLiberoName);
          if (t.libero) newBench.push(t.libero);

          const libIdx = newCourt.length > 6 ? 6 : -1;
          if (libIdx !== -1 && newLiberoName) {
              newCourt[libIdx] = newLiberoName;
          } else if (libIdx !== -1) {
              // Libero taken off without replacement (injury, tactical choice): the
              // back-row middle stays on court until a libero comes back in.
              newCourt.splice(libIdx, 1);
              newCourtRoles.splice(libIdx, 1);
          } else if (newLiberoName) {
              newCourt.push(newLiberoName);
              newCourtRoles.push('Lib');
          }

          // A take-off carries its own marker: the server must not mistake it for a
          // libero change whose incoming player was deleted.
          const zone = !t.libero ? '' : newLiberoName ? `OUT:${t.libero}` : `${LIBERO_OFF_PREFIX}${t.libero}`;
          const libChangeEvent: GameEvent = {
              id: crypto.randomUUID(), time: new Date(), set: currentSet, scoreUs, scoreThem,
              player: newLiberoName ?? '', role: 'Lib', action: 'LIBERO_CHANGE', quality: '0',
              zone, isOpponent: isOpp || undefined,
              videoTimecodeMs: stampVideoTimecodeMs(),
          };

          // One write: the automatic libero swap must see the team and its forcing event together.
          set((state) => ({
              teams: {
                  ...state.teams,
                  [side]: { ...state.teams[side], libero: newLiberoName, bench: newBench, court: newCourt, courtRoles: newCourtRoles },
              },
              currentPointEvents: [...currentPointEvents, libChangeEvent],
          }));
          return libChangeEvent.id;
      },

      cancelLiberoTakeOff: (eventId, targetSide) => {
          const side = targetSide ?? _captureSide();
          const { currentPointEvents, history } = get();
          const event = [...currentPointEvents, ...history].find((e) => e.id === eventId);
          const name = event?.action === 'LIBERO_CHANGE' ? liberoTakenOffName(event.zone) : null;
          if (!name) return;
          const t = _team(side);
          // Only while that libero is still on the bench: anything done since wins.
          if (t.libero || !t.bench.includes(name)) return;
          _setTeam(side, {
              libero: name,
              bench: t.bench.filter((p) => p !== name),
              court: [...t.court.slice(0, 6), name],
              courtRoles: [...t.courtRoles.slice(0, 6), 'Lib'],
          });
          set({
              currentPointEvents: currentPointEvents.filter((e) => e.id !== eventId),
              history: history.filter((e) => e.id !== eventId),
          });
      },

      rotate: (targetSide) => {
        const side = targetSide ?? _captureSide();
        _rotateTeam(side);
        if (side === 'us') {
          set({ isP1SwapActive: false });
          const s = get();
          if (s.selectedAction === 'Attaque' && s.selectedPlayer) {
            set({ selectedPassType: _defaultPassForAttack(s.selectedPlayer, 'Attaque') });
          }
        }
      },

      updateEvent: (id: string, updates) => {
          const { currentPointEvents } = get();
          const index = currentPointEvents.findIndex(e => e.id === id);
          if (index === -1) return;

          const updatedEvent = { ...currentPointEvents[index], ...updates };

          const isTerminal =
              updatedEvent.quality === '=' ||
              (updatedEvent.quality === '#' && ['Service', 'Attaque', 'Bloc', 'Retour Direct'].includes(updatedEvent.action)) ||
              (updatedEvent.quality === '/' && SLASH_TERMINAL_ACTIONS.has(updatedEvent.action));

          if (isTerminal) {
              const newEvents = currentPointEvents.slice(0, index + 1);
              newEvents[index] = updatedEvent;

              const isOpp = !!updatedEvent.isOpponent;
              let winner: 'us' | 'them' = 'us';
              if (updatedEvent.quality === '=' || updatedEvent.quality === '/') winner = isOpp ? 'us' : 'them';
              else if (updatedEvent.quality === '#') winner = isOpp ? 'them' : 'us';

              set({ currentPointEvents: newEvents, gamePhase: 'PointEnd', pointWinner: winner, lastTouchPlayer: null });
          } else {
              const newEvents = currentPointEvents.map(e => e.id === id ? { ...e, ...updates } : e);
              set({ currentPointEvents: newEvents });
              // The rally's last action no longer ends it: the point-end screen kept
              // the old winner and counted a point the server no longer scores
              // (VOL-221). The rally resumes after that action instead.
              if (get().gamePhase === 'PointEnd' && index === currentPointEvents.length - 1) {
                  const enabledSet = buildEnabledActionsSet(get().enabledActions);
                  const next = getNextTransition(updatedEvent.action, enabledSet);
                  set({
                      gamePhase: next.phase, selectedAction: next.action, selectedPlayer: null,
                      lastTouchPlayer: updatedEvent.player, pointWinner: null,
                      ...(get().dualTeamCapture
                        ? { activeTeam: deriveActiveTeam(get().servingSide, toDualRallySteps(newEvents)) }
                        : {}),
                  });
              }
          }
      },

      rekeyEvents: (ids) => {
          const renamed: Record<string, string> = {};
          if (ids.length === 0) return renamed;
          const toRekey = new Set(ids);
          set({
              currentPointEvents: get().currentPointEvents.map((e) => {
                  if (!toRekey.has(e.id)) return e;
                  const id = crypto.randomUUID();
                  renamed[e.id] = id;
                  return { ...e, id };
              }),
          });
          return renamed;
      },

      deleteEvent: (id) => {
          const { currentPointEvents, history, servingSide, statTarget } = get();
          // An undo answered after the point was validated: the action already moved
          // to the history. The server removed it, so the tablet must too; the
          // point keeps its winner (the action was not the rally's last one).
          if (!currentPointEvents.some((e) => e.id === id)) {
              const done = history.find((e) => e.id === id);
              if (!done) return;
              if (done.action === 'LIBERO_CHANGE' && liberoTakenOffName(done.zone)) {
                  // Same as during the rally: the server replay puts the libero back.
                  get().cancelLiberoTakeOff(id, done.isOpponent ? 'them' : 'us');
              }
              set({ history: get().history.filter((e) => e.id !== id) });
              return;
          }
          // Read before putting a libero back: that step already drops the event.
          const isLast = currentPointEvents.length > 0 && currentPointEvents[currentPointEvents.length - 1].id === id;
          const target = currentPointEvents.find((e) => e.id === id);
          if (target?.action === 'LIBERO_CHANGE' && liberoTakenOffName(target.zone)) {
              // Deleting a take-off puts the libero back, as the server replay does.
              get().cancelLiberoTakeOff(id, target.isOpponent ? 'them' : 'us');
          }
          const newEvents = currentPointEvents.filter(e => e.id !== id);
          set({
              currentPointEvents: newEvents,
              // The point is won by its last action: removing an earlier one (an undo
              // answered after a rally-ending action was entered) must not leave a
              // PointEnd phase without winner, which `confirmPoint` cannot close.
              ...(isLast ? { pointWinner: null } : {}),
              ...(get().dualTeamCapture
                ? { activeTeam: deriveActiveTeam(servingSide, toDualRallySteps(newEvents)) }
                : {}),
          });

          if (isLast) {
              if (newEvents.length === 0) {
                  const isOpp = statTarget === 'them';
                  // Two teams: every rally opens on the serving team's serve (as in startSet).
                  const targetIsServing = get().dualTeamCapture
                      || (isOpp ? (servingSide === 'them') : (servingSide === 'us'));
                  set({
                      gamePhase: targetIsServing ? 'Serve' : 'Reception',
                      selectedAction: targetIsServing ? 'Service' : 'Réception',
                      selectedPlayer: null, lastTouchPlayer: null
                  });
              } else {
                  const lastEvent = newEvents[newEvents.length - 1];
                  const enabledSet = buildEnabledActionsSet(get().enabledActions);
                  const next = getNextTransition(lastEvent.action, enabledSet);
                  // next.action est CycleAction, sous-ensemble de ActionType : affectation directe OK.
                  set({ gamePhase: next.phase, selectedAction: next.action, selectedPlayer: null, lastTouchPlayer: lastEvent.player });
              }
          }
      },

      confirmPoint: () => {
          const { pointWinner, scoreUs, scoreThem, servingSide, history, currentPointEvents, currentSet, setScores, statTarget, setsToWinMatch } = get();
          if (!pointWinner) return;

          const winner = pointWinner;
          const newHistory = [...history, ...currentPointEvents];

          let newScoreUs = scoreUs;
          let newScoreThem = scoreThem;
          let newServingSide = servingSide;

          if (winner === 'us') {
              newScoreUs++;
              if (servingSide === 'them') _rotateTeam('us');
              newServingSide = 'us';
          } else {
              newScoreThem++;
              if (servingSide === 'us') _rotateTeam('them');
              newServingSide = 'them';
          }

          const usCourtRoles = _team('us').courtRoles;
          const setOver = isSetOver(newScoreUs, newScoreThem, currentSet, setsToWinMatch);

          if (setOver) {
              const finalSetScores = { ...setScores, [currentSet]: { us: newScoreUs, them: newScoreThem } };
              let setsWonUs = 0;
              let setsWonThem = 0;
              Object.values(finalSetScores).forEach(s => {
                  if (s.us > s.them) setsWonUs++;
                  else if (s.them > s.us) setsWonThem++;
              });
              const matchOver = isMatchOver(setsWonUs, setsWonThem, setsToWinMatch);

              set({
                  scoreUs: newScoreUs, scoreThem: newScoreThem, servingSide: newServingSide,
                  history: newHistory, currentPointEvents: [], lastTouchPlayer: null,
                  selectedPlayer: null, pointWinner: null,
                  gamePhase: matchOver ? 'MatchEnd' : 'SetEnd',
                  setScores: finalSetScores,
                  setInfos: {
                      ...get().setInfos,
                      [currentSet]: { ...get().setInfos[currentSet], timeOutsUs: get().timeOutsUs, timeOutsThem: get().timeOutsThem }
                  }
              });
          } else {
              // Deux équipes : le rallye s'ouvre TOUJOURS sur le service de l'équipe
              // qui sert (l'équipe active suit `servingSide`). Sans ça, un point
              // gagné par l'adversaire enchaînait sur « Réception » et son service
              // n'était jamais saisi. Une équipe : comportement inchangé.
              const isOpp = statTarget === 'them';
              const targetIsServing = get().dualTeamCapture
                ? true
                : (isOpp ? (newServingSide === 'them') : (newServingSide === 'us'));

              const usRotAfterPoint = getRotationLabelFromCourtRoles(usCourtRoles);
              set({
                  scoreUs: newScoreUs, scoreThem: newScoreThem, servingSide: newServingSide,
                  gamePhase: targetIsServing ? 'Serve' : 'Reception',
                  isP1SwapActive: shouldApplyP1SideOutAttackSwap(usRotAfterPoint, newServingSide),
                  currentPointEvents: [], lastTouchPlayer: null, selectedPlayer: null,
                  selectedAction: targetIsServing ? 'Service' : 'Réception',
                  history: newHistory, pointWinner: null,
                  ...(get().dualTeamCapture ? { activeTeam: initialActiveTeam(newServingSide) } : {}),
              });
          }
          notifyPointConfirmed();
      },

      cancelPointEnd: () => {
          const { currentPointEvents } = get();
          if (currentPointEvents.length > 0) {
              const nextEvents = currentPointEvents.slice(0, -1);
              set({
                  gamePhase: 'Block', pointWinner: null, currentPointEvents: nextEvents,
                  ...(get().dualTeamCapture
                    ? { activeTeam: deriveActiveTeam(get().servingSide, toDualRallySteps(nextEvents)) }
                    : {}),
              });
          } else {
              set({ gamePhase: 'Block', pointWinner: null });
          }
      },

      nextSet: () => {
          const { currentSet } = get();
          if (currentSet >= 5) return;
          // L'équipe active est remise sur `home` : le set précédent pouvait se
          // terminer sur un rallye adverse, et l'écran de composition suit
          // l'équipe active.
          set({
              gamePhase: 'SetSetup',
              currentSet: currentSet + 1,
              ...(get().dualTeamCapture ? { activeTeam: 'home' as TeamSide } : {}),
          });
      },

      designateSetter: (courtIndex: number, targetSide) => {
          const side = targetSide ?? _captureSide();
          const t = _team(side);
          if (courtIndex < 0 || courtIndex >= t.court.length) return;

          const newCourtRoles: PlayerRole[] = t.courtRoles.map((role, i) => {
              if (i === courtIndex) return 'Passeur';
              if (role === 'Passeur') {
                  const naturalRole = t.playerRoles[t.court[i]] ?? 'R4';
                  return naturalRole === 'Passeur' ? 'R4' : naturalRole;
              }
              return role;
          });
          _setTeam(side, { courtRoles: newCourtRoles });
      },

      setCourtRole: (courtIndex, role, targetSide) => {
          const side = targetSide ?? _captureSide();
          const t = _team(side);
          if (courtIndex < 0 || courtIndex >= t.court.length) return;
          // Un seul passeur sur le terrain : la rotation s'en déduit (`P1`…`P6`).
          // `designateSetter` tient déjà cet invariant, on ne le réécrit pas.
          if (role === 'Passeur') {
              get().designateSetter(courtIndex, side);
              return;
          }
          const newCourtRoles = [...t.courtRoles];
          newCourtRoles[courtIndex] = role;
          _setTeam(side, { courtRoles: newCourtRoles });
      },

      startSet: (court, courtRoles, bench, awayLineup, liberos) => {
          // Deux équipes : la compo reçue est TOUJOURS celle de notre équipe
          // (`us`), l'adversaire arrive par `awayLineup`. Sans cette garde, un
          // `statTarget` inattendu réécrivait notre effectif avec les joueurs
          // adverses — d'où l'« adversaire contre adversaire » au set suivant.
          const side: 'us' | 'them' = get().dualTeamCapture ? 'us' : get().statTarget;
          const { currentSet, setInfos, history, setScores } = get();

          // Règle de jeu : le premier serveur ALTERNE d'un set à l'autre. Le set
          // repartait systématiquement sur « nous au service », ce qui faussait les
          // phases K1/K2 de tout le set tant que personne ne pensait à cliquer
          // « Réception ». Le set décisif fait l'objet d'un nouveau tirage au sort :
          // l'alternance n'y est qu'un défaut, corrigeable comme avant.
          const previousFirstServer = setInfos[currentSet - 1]?.firstServer;
          const firstServer: 'us' | 'them' =
              previousFirstServer === 'us' ? 'them' : previousFirstServer === 'them' ? 'us' : 'us';
          // Même convention que `setServingSide` : la phase décrit ce que fait
          // l'équipe SAISIE. En scouting (`statTarget = 'them'`) ou en deux équipes,
          // ce n'est pas forcément nous.
          const targetServesFirst = get().dualTeamCapture || firstServer === side;

          const newSetInfos = {
              ...setInfos,
              [currentSet]: { startingLineup: [...court], timeOutsUs: 0, timeOutsThem: 0, firstServer }
          };
          // Deux équipes (VS-12 Lot 3) : on embarque la compo de départ de l'équipe B
          // dans le `SET_START` (persisté verbatim côté backend) → lineup + roster B
          // restaurés même sur un nouvel appareil (cross-device), en plus de la
          // persistance localStorage. Les clés extra sont ignorées par les autres lecteurs.
          const dual = get().dualTeamCapture;
          // La compo B est posée AVANT de construire la charge `SET_START` :
          // c'est elle qui sera persistée verbatim, et relue au restore.
          // Compo B vide (aucun roster adverse saisi) : on garde ce que le store
          // avait plutôt que d'effacer l'équipe B.
          if (dual && awayLineup && awayLineup.court.length > 0) {
              _setTeam('them', {
                  court: awayLineup.court,
                  courtRoles: awayLineup.courtRoles,
                  bench: awayLineup.bench,
                  // Le libéro choisi dans la case libéro de la compo (7e place).
                  libero: awayLineup.court[6] ?? null,
                  liberos: awayLineup.liberos ?? null,
              });
          }
          const them = get().teams.them;
          const zonePayload: Record<string, unknown> = { court, courtRoles, bench };
          if (liberos) zonePayload.liberos = liberos;
          // The server closes the previous set on this score when its replay does
          // not see that set as over (VOL-214): the tablet's score is the truth.
          const closingScore = setScores[currentSet - 1];
          if (closingScore) zonePayload.closingScore = closingScore;
          if (dual) {
              zonePayload.dual = true;
              zonePayload.away = {
                  court: them.court,
                  courtRoles: them.courtRoles,
                  bench: them.bench,
                  libero: them.libero,
                  roster: them.roster,
                  ...(them.liberos ? { liberos: them.liberos } : {}),
              };
          }
          const setStartEvent: GameEvent = {
              id: crypto.randomUUID(), time: new Date(), set: currentSet,
              scoreUs: 0, scoreThem: 0, player: '', role: '',
              action: 'SET_START', quality: '0',
              zone: JSON.stringify(zonePayload),
              videoTimecodeMs: stampVideoTimecodeMs(),
          };

          // Le libéro suit la case libéro de la compo (7e place du terrain) : il
          // peut changer d'un set à l'autre, ou ne pas être aligné.
          _setTeam(side, { court, courtRoles, bench, libero: court[6] ?? null, liberos: liberos ?? null });
          set({
              setInfos: newSetInfos, scoreUs: 0, scoreThem: 0,
              timeOutsUs: 0, timeOutsThem: 0, currentPointEvents: [],
              history: [...history, setStartEvent], servingSide: firstServer,
              gamePhase: targetServesFirst ? 'Serve' : 'Reception',
              selectedAction: targetServesFirst ? 'Service' : 'Réception',
              selectedPlayer: null, lastTouchPlayer: null,
              isP1SwapActive: shouldApplyP1SideOutAttackSwap(
                  getRotationLabelFromCourtRoles(_team('us').courtRoles),
                  firstServer,
              ),
              ownStartingLineupValidated: true,
              ...(get().dualTeamCapture ? { activeTeam: initialActiveTeam(firstServer) } : {}),
          });
      },

      skipToScore: (targetSet, targetScoreUs, targetScoreThem, targetServingSide, pastSetScores) => {
          const usCourt = _team('us').court;
          const usCourtRoles = _team('us').courtRoles;
          const { setInfos, setScores, history, currentSet, scoreUs, scoreThem, setsToWinMatch } = get();
          const keptHistory = history.filter(e => e.set < targetSet);

          const skipEvent: GameEvent = {
              id: crypto.randomUUID(), time: new Date(), set: targetSet,
              scoreUs: targetScoreUs, scoreThem: targetScoreThem,
              player: '', role: '', action: 'SKIP_TO_SCORE', quality: '0', zone: '',
              videoTimecodeMs: stampVideoTimecodeMs(),
          };
          const newHistory = [...keptHistory, skipEvent];

          const currentRotation = getRotationLabelFromCourtRoles(usCourtRoles);
          const isP1SwapActive = shouldApplyP1SideOutAttackSwap(currentRotation, targetServingSide);

          const newSetInfos = {
              ...setInfos,
              [targetSet]: { startingLineup: [...usCourt], timeOutsUs: 0, timeOutsThem: 0, firstServer: targetServingSide }
          };

          const previousSetScores: Record<number, { us: number; them: number }> = {};
          for (const [setNum, score] of Object.entries(setScores)) {
              if (Number(setNum) < targetSet) previousSetScores[Number(setNum)] = score;
          }
          if (pastSetScores) {
              for (const [setNum, score] of Object.entries(pastSetScores)) {
                  if (Number(setNum) < targetSet) previousSetScores[Number(setNum)] = score;
              }
          } else if (targetSet > currentSet && !previousSetScores[currentSet] && (scoreUs > 0 || scoreThem > 0)) {
              previousSetScores[currentSet] = { us: scoreUs, them: scoreThem };
          }

          const setOver = isSetOver(targetScoreUs, targetScoreThem, targetSet, setsToWinMatch);

          if (setOver) {
              const finalSetScores = { ...previousSetScores, [targetSet]: { us: targetScoreUs, them: targetScoreThem } };
              skipEvent.zone = JSON.stringify({ setScores: finalSetScores, currentSet: targetSet, servingSide: targetServingSide });
              let setsWonUs = 0; let setsWonThem = 0;
              Object.values(finalSetScores).forEach(s => { if (s.us > s.them) setsWonUs++; else if (s.them > s.us) setsWonThem++; });
              const matchOver = isMatchOver(setsWonUs, setsWonThem, setsToWinMatch);

              set({
                  currentSet: targetSet, scoreUs: targetScoreUs, scoreThem: targetScoreThem,
                  setScores: finalSetScores, setInfos: newSetInfos, servingSide: targetServingSide,
                  timeOutsUs: 0, timeOutsThem: 0, history: newHistory, currentPointEvents: [],
                  gamePhase: matchOver ? 'MatchEnd' : 'SetEnd',
                  selectedPlayer: null, lastTouchPlayer: null, pointWinner: null, isP1SwapActive
              });
          } else {
              skipEvent.zone = JSON.stringify({ setScores: previousSetScores, currentSet: targetSet, servingSide: targetServingSide });
              // Deux équipes : reprise sur le service de l'équipe qui sert (cf. confirmPoint).
              const isOpp = get().statTarget === 'them';
              const skipTargetIsServing = get().dualTeamCapture
                ? true
                : (isOpp ? (targetServingSide === 'them') : (targetServingSide === 'us'));

              set({
                  currentSet: targetSet, scoreUs: targetScoreUs, scoreThem: targetScoreThem,
                  setScores: previousSetScores, setInfos: newSetInfos, servingSide: targetServingSide,
                  timeOutsUs: 0, timeOutsThem: 0, history: newHistory, currentPointEvents: [],
                  gamePhase: skipTargetIsServing ? 'Serve' : 'Reception',
                  selectedAction: skipTargetIsServing ? 'Service' : 'Réception',
                  selectedPlayer: null, lastTouchPlayer: null, pointWinner: null, isP1SwapActive,
                  ...(get().dualTeamCapture ? { activeTeam: initialActiveTeam(targetServingSide) } : {}),
              });
          }
      },

      selectPlayer: (name, options) => {
          const { selectedAction, gamePhase } = get();
          const stampSelection = () => {
              if (options?.auto) {
                  playerSelectionStamp = null;
                  return;
              }
              const moment = options?.at ?? { time: new Date(), videoTimecodeMs: stampVideoTimecodeMs() };
              playerSelectionStamp = {
                  player: name,
                  time: moment.time,
                  videoTimecodeMs: moment.videoTimecodeMs,
                  given: options?.at !== undefined,
                  pointEvents: get().currentPointEvents,
                  history: get().history,
              };
          };
          // Avant/arrière évalué sur la grille du côté en saisie : en deux équipes,
          // un joueur adverse doit basculer Bloc/Défense selon SA ligne, pas la nôtre.
          const visualCourt = get().getVisualCourt(_captureSide());
          let newAction = selectedAction;

          if (gamePhase === 'Block') {
              // Attaque touchée au bloc : le bloqueur DOIT être désigné. Sans cette
              // garde, cliquer un arrière basculait l'action sur « Défense » et
              // permettait de contourner la désignation que la cible venait de
              // réclamer.
              const blockedAttack = lastNonPhantomEvent(get().currentPointEvents);
              const blockerRequired = mustDesignateBlocker(
                  blockedAttack,
                  buildEnabledActionsSet(get().enabledActions),
                  get().dualTeamCapture,
              );
              const playerIndex = visualCourt.indexOf(name);
              const isFrontRow = [1, 2, 3].includes(playerIndex);
              // Un arrière ne bloque pas : quand la désignation du bloqueur est
              // réclamée, le clic sur un joueur de la ligne arrière est ignoré —
              // seuls les trois avant sont sélectionnables. Sans cette garde, on
              // pouvait attribuer le contre à un joueur qui n'était pas au filet.
              if (blockerRequired && !isFrontRow) return;
              // Désignation du bloqueur : un clic suffit. La qualité du contre se
              // déduit de ce que la cible de l'attaque a déjà dit (où est parti le
              // ballon) plutôt que de réclamer un geste de plus au milieu d'un échange.
              // Saisie clavier exclue : sa séquence joueur → qualité commit elle-même,
              // valider ici produirait un second `Bloc`.
              if (blockerRequired && isFrontRow && get().inputSurface === 'touch') {
                stampSelection();
                set({ selectedPlayer: name, selectedAction: 'Bloc', selectedPassType: null });
                get().commitAction(blockerDesignationQuality(blockedAttack));
                return;
              }
              if (!blockerRequired && !isFrontRow && selectedAction === 'Bloc') newAction = 'Défense';
              else if (isFrontRow && selectedAction === 'Défense') newAction = 'Bloc';
          }
          stampSelection();
          set({
              selectedPlayer: name,
              selectedAction: newAction,
              selectedPassType: _defaultPassForAttack(name, newAction),
          });
      },

      selectMiddleBlockerCall: (call) => set({ selectedMiddleBlockerCall: call }),
      selectPassType: (type) => set({ selectedPassType: type }),
      selectServiceType: (type) => set({ selectedServiceType: type }),
      selectBlockersCount: (count) => set({ selectedBlockersCount: count }),
      selectIsReversal: (val) => set({ selectedIsReversal: val }),

      selectAction: (action) => {
          const { scoreUs, scoreThem, currentPointEvents, currentSet, setInfos, selectedPlayer } = get();
          const isStartOfSet = scoreUs === 0 && scoreThem === 0 && currentPointEvents.length === 0;

          if (isStartOfSet) {
              const isOpp = get().statTarget === 'them';
              const usCourtRoles = _team('us').courtRoles;

              if (action === 'Service') {
                  const newServingSide = isOpp ? 'them' : 'us';
                  set({
                      selectedAction: action,
                      servingSide: newServingSide,
                      gamePhase: 'Serve',
                      isP1SwapActive: false,
                      selectedPassType: null,
                      ...(get().dualTeamCapture ? { activeTeam: initialActiveTeam(newServingSide) } : {}),
                  });
                  const info = setInfos[currentSet];
                  if (info) set({ setInfos: { ...setInfos, [currentSet]: { ...info, firstServer: newServingSide } } });
              } else if (action === 'Réception') {
                  const newServingSide = isOpp ? 'us' : 'them';
                  const currentRotation = getRotationLabelFromCourtRoles(usCourtRoles);
                  const p1Swap = !isOpp && shouldApplyP1SideOutAttackSwap(currentRotation, newServingSide);
                  set({
                      selectedAction: action,
                      servingSide: newServingSide,
                      gamePhase: 'Reception',
                      isP1SwapActive: p1Swap,
                      selectedPassType: null,
                      ...(get().dualTeamCapture ? { activeTeam: initialActiveTeam(newServingSide) } : {}),
                  });
                  const info = setInfos[currentSet];
                  if (info) set({ setInfos: { ...setInfos, [currentSet]: { ...info, firstServer: newServingSide } } });
              } else {
                  set({
                      selectedAction: action,
                      selectedPassType: _defaultPassForAttack(selectedPlayer, action),
                  });
              }
          } else {
              set({
                  selectedAction: action,
                  selectedPassType: action === 'Attaque' ? _defaultPassForAttack(selectedPlayer, action) : null,
              });
          }
      },

      commitAction: (quality: ActionQuality, zone?: string, target?: { x: number; y: number; touched?: boolean }, origin?: { x: number; y: number }) => {
        const {
            currentSet, scoreUs, scoreThem, currentPointEvents, history,
            selectedPlayer, selectedAction, selectedMiddleBlockerCall, selectedPassType,
            selectedServiceType, selectedIsReversal, selectedBlockersCount,
            gamePhase, servingSide, statTarget,
            dualTeamCapture, activeTeam
        } = get();

        // En saisie deux équipes, le côté capturé suit l'équipe active (home=nous,
        // away=adversaire) ; sinon on garde le comportement une équipe (statTarget).
        const captureSide: 'us' | 'them' = dualTeamCapture
          ? (activeTeam === 'home' ? 'us' : 'them')
          : statTarget;
        const isOpp = captureSide === 'them';
        const t = _team(captureSide);

        if (!selectedPlayer && gamePhase !== 'Serve') return;
        const player = selectedPlayer || (gamePhase === 'Serve' ? t.court[0] : 'Inconnu');

        let currentRotation = '??';
        const setterIndex = t.courtRoles.findIndex(r => r === 'Passeur');
        if (setterIndex !== -1) currentRotation = `P${setterIndex + 1}`;

        // Le joueur reste « dernière touche » (grisé) tant que la touche suivante
        // lui est interdite. Les actions qui envoient le ballon de l'autre côté du
        // filet (ou le bloc) libèrent le verrou : c'est un autre joueur qui reprend.
        let newLastTouch: string | null = player;
        if (['Service', 'Attaque', 'Freeball Donnée', 'Retour Direct', 'Bloc'].includes(selectedAction)) newLastTouch = null;

        const currentPhase: 'K1' | 'K2' = isOpp
          ? (servingSide === 'us' ? 'K1' : 'K2')
          : (servingSide === 'us' ? 'K2' : 'K1');

        const courtIdx = t.court.indexOf(player);
        // Role played this rally under the system: in a 6-2 the front-row setter attacks as
        // the opposite. The rotation above keeps following the designated setter.
        const rallyRoles = playingRoles(t, _tacticsFor(captureSide).gameSystem);
        const tacticalRole = courtIdx !== -1 ? rallyRoles[courtIdx] : (player === t.libero ? 'Lib' : t.playerRoles[player]);
        // Rotation slot (1-6), not the displayed spot after the switch: in a 6-2 the rotation only
        // follows the first setter, so the stats read the second setter's front row from it (VOL-321).
        const courtPosition = courtIdx >= 0 && courtIdx < 6 ? courtIdx + 1 : undefined;

        // Attaque dont la cible tombe dans le camp de l'attaquant : le contre l'a
        // renvoyée. Le ballon reste jouable de CE côté (qualité ! ou -), c'est donc
        // sa couverture qui enchaîne, une fois le bloqueur désigné.
        // Sens de la cible : seule la saisie deux équipes injecte l'équipe adverse
        // dans la moitié haute du terrain et inverse donc le repère. En une équipe
        // — la nôtre comme l'adverse en scouting — l'équipe saisie est rendue en
        // bas et attaque vers le haut (même convention que les boutons qualité).
        const targetSide: 'us' | 'them' =
          dualTeamCapture && captureSide === 'them' ? 'them' : 'us';
        const attackBlockedBack =
          selectedAction === 'Attaque' &&
          !!target &&
          getTargetStatus(target.x, target.y, 'Attaque', targetSide) === 'BLOCKED';

        const eventStamp = takeActionStamp(selectedAction, selectedPlayer, currentPointEvents, history);
        const newEvent: GameEvent = {
          id: crypto.randomUUID(), time: eventStamp.time, set: currentSet, scoreUs, scoreThem, player,
          role: tacticalRole || '-', action: selectedAction, quality,
          middleBlockerCall: selectedMiddleBlockerCall || undefined,
          passType: selectedPassType || undefined,
          serviceType: selectedServiceType || undefined,
          // Nombre de bloqueurs : information d'attaque uniquement, et seulement
          // si le scoreur l'a renseignée (`null` = non saisi, ≠ « 0 bloqueur »).
          blockersCount:
            selectedAction === 'Attaque' && selectedBlockersCount !== null
              ? selectedBlockersCount
              : undefined,
          isReversal: selectedIsReversal || undefined,
          zone: zone || '', rotation: currentRotation, courtPosition, phase: currentPhase,
          targetX: target?.x, targetY: target?.y, originX: origin?.x, originY: origin?.y,
          // Une cible dans son propre camp (BLOCKED) DIT que le ballon est passé
          // dans le bloc : le bandeau « touché ? » n'est même pas proposé dans ce
          // cas. On pose donc l'information nous-mêmes, sinon la désignation du
          // bloqueur — qui s'appuie dessus — ne se déclencherait jamais.
          touchedByBlock: target?.touched ?? (attackBlockedBack || undefined),
          isOpponent: isOpp || undefined,
          // En deux équipes, on stampe l'équipe active (convergence VS-12) ; en une
          // équipe on laisse `gameEventToDto` dériver teamSide depuis `isOpponent`.
          teamSide: dualTeamCapture ? activeTeam : undefined,
          videoTimecodeMs: eventStamp.videoTimecodeMs,
        };

        set({
          selectedMiddleBlockerCall: null, selectedPassType: null, selectedServiceType: null,
          selectedIsReversal: false, selectedBlockersCount: null,
        });

        let nextPhase = gamePhase;
        let nextPointWinner: 'us' | 'them' | null = null;
        // Équipe active imposée après cette action, quand la seule règle de
        // traversée ne suffit pas (contre renvoyé dans le camp de l'attaquant).
        let nextActiveTeam: TeamSide | null = null;

        if (quality === '=') {
            nextPointWinner = isOpp ? 'us' : 'them';
            nextPhase = 'PointEnd';
        } else if (quality === '#' && ['Service', 'Attaque', 'Bloc', 'Retour Direct'].includes(selectedAction)) {
            nextPointWinner = isOpp ? 'them' : 'us';
            nextPhase = 'PointEnd';
        } else {
            let nextAction: ActionType = selectedAction;
            let nextPlayer: string | null = null;
            const enabledSet = buildEnabledActionsSet(get().enabledActions);

            if (quality === '/') {
                if (SLASH_TERMINAL_ACTIONS.has(selectedAction)) {
                    // Attack stuffed by the block, or block invasion (VOL-189): the
                    // rally ends in every mode, two-team capture included. Designating
                    // the blocker afterwards used to stretch a finished rally; a ball
                    // that stays in play is a *touched* block, which goes through the
                    // `Block` phase without being a "/".
                    nextPointWinner = isOpp ? 'us' : 'them';
                    nextPhase = 'PointEnd';
                } else if (dualTeamCapture && isDirectReturn(selectedAction, quality)) {
                    // Renvoi direct : le ballon repart par-dessus le filet et
                    // l'équipe d'en face récupère une balle facile. La main lui passe
                    // sur sa freeball reçue.
                    //
                    // On restait jusqu'ici du même côté en proposant un bloc, alors
                    // que personne n'attaquait : le ballon venait d'arriver chez
                    // l'adversaire. En une équipe le bloc garde son sens — c'est NOTRE
                    // bloc sur l'attaque adverse qui suit, et l'équipe d'en face n'est
                    // pas saisie.
                    const next = nextDualTransition(selectedAction, enabledSet, quality);
                    nextPhase = next.phase;
                    nextAction = next.action as ActionType;
                    nextActiveTeam = flipSide(activeTeam);
                } else {
                    const next = findNextEnabledInCycle('Bloc', enabledSet);
                    nextPhase = next.phase;
                    nextAction = next.action;
                }
            } else if (dualTeamCapture && isTraversingAction(selectedAction)) {
                // Deux équipes : l'action traverse le filet → la main passe à
                // l'équipe adverse sur son action d'entrée (réception / défense).
                let next = nextDualTransition(selectedAction, enabledSet);
                // Mode expert : la cible dit si le ballon est passé DANS le bloc.
                // Touché → le bloc doit être saisi pour désigner le bloqueur ; pas
                // touché → inutile de le demander, l'équipe d'en face enchaîne sur sa
                // défense (une action de moins à remplir). Sans cible (mode standard)
                // l'information n'existe pas : le cycle reste inchangé.
                const blockUntouched =
                    selectedAction === 'Attaque' && !!target && !attackBlockedBack
                    && target.touched !== true;
                if (next.action === 'Bloc' && blockUntouched) {
                    next = findNextEnabledInCycle('Défense', enabledSet);
                }
                // Attaque renvoyée dans le camp de l'attaquant : le ballon ne reste
                // pas du côté qui bloque. On y désigne seulement le bloqueur (`Bloc`,
                // équipe d'en face) ; si le bloc n'est pas suivi, on rend la main tout
                // de suite à l'attaquant pour sa couverture.
                if (attackBlockedBack && next.action !== 'Bloc') {
                    next = findNextEnabledInCycle('Défense', enabledSet);
                    nextActiveTeam = activeTeam;
                }
                nextPhase = next.phase;
                nextAction = next.action as ActionType;
            } else if (
                dualTeamCapture &&
                selectedAction === 'Bloc' &&
                (quality === '!' || isAttackBlockedBackToAttacker(lastNonPhantomEvent(currentPointEvents)))
            ) {
                // Bloqueur désigné sur une attaque renvoyée chez l'attaquant : la
                // balle est déjà repartie de l'autre côté du filet. On rebascule donc
                // sur l'équipe qui attaquait, pour sa couverture (« soutien »).
                const next = findNextEnabledInCycle('Défense', enabledSet);
                nextPhase = next.phase;
                nextAction = next.action;
                nextActiveTeam = flipSide(activeTeam);
            } else if (selectedAction === 'Bloc' && quality === '!') {
                // Covered block (VOL-189): the attacker plays the ball again, so our
                // block comes next, as the former block "/" (Soutien) did.
                const next = findNextEnabledInCycle('Bloc', enabledSet);
                nextPhase = next.phase;
                nextAction = next.action;
            } else {
                let next = getNextTransition(selectedAction, enabledSet);
                if (
                    !isOpp &&
                    selectedAction === 'Attaque' &&
                    target &&
                    getTargetStatus(target.x, target.y, 'Attaque') === 'BLOCKED' &&
                    (quality === '!' || quality === '-')
                ) {
                    next = findNextEnabledInCycle('Défense', enabledSet);
                } else if (isTransitionSkipped(selectedAction, enabledSet)) {
                    // Touche intermédiaire non statée (ex. Passe désactivée) : le
                    // ballon est passé par un autre joueur entre les deux actions
                    // saisies → le dernier toucheur redevient sélectionnable.
                    newLastTouch = null;
                }
                nextPhase = next.phase;
                nextAction = next.action;
            }

            if (!nextPointWinner) {
                if (nextAction === 'Passe') {
                    const setter = t.court.find((p, i) => t.courtRoles[i] === 'Passeur');
                    const lib = t.court.find((p, i) => t.courtRoles[i] === 'Lib') || t.libero;
                    if (player === setter && lib) nextPlayer = lib;
                    else if (setter && setter !== player) nextPlayer = setter;
                }
                set({
                    selectedAction: nextAction,
                    selectedPlayer: nextPlayer,
                    selectedPassType: _defaultPassForAttack(nextPlayer, nextAction),
                });
            }
        }

        set({
            currentPointEvents: [...currentPointEvents, newEvent],
            gamePhase: nextPhase, lastTouchPlayer: newLastTouch, pointWinner: nextPointWinner,
            // Deux équipes : l'équipe active bascule sur les actions traversantes
            // (ou suit l'exception du contre renvoyé chez l'attaquant).
            ...(dualTeamCapture
              ? { activeTeam: nextActiveTeam ?? advanceActiveTeam(activeTeam, selectedAction) }
              : {}),
        });
      },

      commitOpponentAction: (winner, reason, player) => {
          const { currentSet, scoreUs, scoreThem, currentPointEvents, history, servingSide, statTarget, teams, dualTeamCapture } = get();
          // Équipe AUTRICE de la ligne. En deux équipes, « point / faute
          // adversaire » est stampé `away` : sa rotation et sa phase doivent être
          // celles de l'équipe B. Elles venaient de `_active()`, donc de la nôtre,
          // et faussaient ses stats par rotation.
          // A typed fault (Porté, Filet…) belongs to the side that loses the
          // point, which may be ours: it used to be stamped as an opponent
          // action, so our player's fault went to the other team's stats.
          const isTypedFault = (TEAM_FAULT_ACTIONS as readonly string[]).includes(reason);
          const authorSide: 'us' | 'them' = isTypedFault
            ? (winner === 'us' ? 'them' : 'us')
            : (dualTeamCapture ? 'them' : statTarget);
          const isOpp = authorSide === 'them';
          const t = _team(authorSide);

          const currentPhase: 'K1' | 'K2' = isOpp
            ? (servingSide === 'us' ? 'K1' : 'K2')
            : (servingSide === 'us' ? 'K2' : 'K1');

          let currentRotation = '??';
          const setterIndex = t.courtRoles.findIndex(r => r === 'Passeur');
          if (setterIndex !== -1) currentRotation = `P${setterIndex + 1}`;

          // A position fault may be recorded without a player, on our side too:
          // it must not read as an opponent's line in the history.
          const eventPlayer = player || (isOpp ? 'Adversaire' : 'Inconnu');
          let eventRole = 'Opp';
          if (player) {
              const usIdx = teams.us.court.indexOf(player);
              if (usIdx !== -1) {
                  eventRole = teams.us.courtRoles[usIdx] || teams.us.playerRoles[player] || 'Inconnu';
              } else {
                  const themIdx = teams.them.court.indexOf(player);
                  if (themIdx !== -1) {
                      eventRole = teams.them.courtRoles[themIdx] || 'Inconnu';
                  } else {
                      const oppPlayer = teams.them.roster.find(p => p.name === player);
                      eventRole = oppPlayer?.role || teams.us.playerRoles[player] || 'Inconnu';
                  }
              }
          }

          // Typed fault on the selected player (quick buttons): the tap on the
          // player is the moment of the fault, like any other player action.
          const eventStamp = takeEventStamp(player ?? null, currentPointEvents, history);
          const newEvent: GameEvent = {
              id: crypto.randomUUID(), time: eventStamp.time, set: currentSet, scoreUs, scoreThem,
              player: eventPlayer, role: eventRole, action: reason,
              // A typed fault is always an error of its author: `#` on an
              // opponent's Porté hid it from the scouted team's fault counts.
              quality: isTypedFault ? '=' : (winner === 'us' ? '#' : '='), zone: '', rotation: currentRotation, phase: currentPhase,
              isOpponent: isTypedFault ? (isOpp || undefined) : true,
              // `Faute Adversaire` / `Point Adversaire` décrivent une action de
              // l'équipe adverse : en deux équipes on la stampe explicitement
              // `away`, faute de quoi la convergence A/B la rattacherait à
              // l'équipe active — donc à nous quand c'est notre tour de saisir.
              teamSide: dualTeamCapture ? (isOpp ? 'away' : 'home') : undefined,
              videoTimecodeMs: eventStamp.videoTimecodeMs,
          };
          set({ currentPointEvents: [...currentPointEvents, newEvent], gamePhase: 'PointEnd', pointWinner: winner, lastTouchPlayer: null });
      },

      commitTeamFault: (faultType, player, side) => {
          const {
              currentSet, scoreUs, scoreThem, currentPointEvents,
              selectedPlayer, servingSide, statTarget, dualTeamCapture,
          } = get();
          const activeTeam = side ?? get().activeTeam;
          const captureSide = teamFaultSide({ dualTeamCapture, activeTeam: get().activeTeam, statTarget }, side);
          const isOpp = captureSide === 'them';
          const t = _team(captureSide);

          // La faute appartient au côté fautif → point à l'équipe adverse.
          const winner: 'us' | 'them' = isOpp ? 'us' : 'them';

          // A position fault is the team's: never pinned on a guessed player.
          const eventPlayer = faultType === POSITION_FAULT_ACTION
            ? (player || 'Inconnu')
            : (player || selectedPlayer || t.court[0] || 'Inconnu');
          let currentRotation = '??';
          const setterIndex = t.courtRoles.findIndex(r => r === 'Passeur');
          if (setterIndex !== -1) currentRotation = `P${setterIndex + 1}`;

          const currentPhase: 'K1' | 'K2' = isOpp
            ? (servingSide === 'us' ? 'K1' : 'K2')
            : (servingSide === 'us' ? 'K2' : 'K1');

          const courtIdx = t.court.indexOf(eventPlayer);
          const role = courtIdx !== -1
            ? t.courtRoles[courtIdx]
            : (eventPlayer === t.libero ? 'Lib' : t.playerRoles[eventPlayer]);

          const eventStamp = takeEventStamp(eventPlayer, currentPointEvents, get().history);
          const newEvent: GameEvent = {
              id: crypto.randomUUID(), time: eventStamp.time, set: currentSet, scoreUs, scoreThem,
              player: eventPlayer, role: role || '-', action: faultType, quality: '=',
              zone: '', rotation: currentRotation, phase: currentPhase,
              isOpponent: isOpp || undefined,
              // Deux équipes : on stampe l'équipe active (convergence VS-12).
              teamSide: dualTeamCapture ? activeTeam : undefined,
              videoTimecodeMs: eventStamp.videoTimecodeMs,
          };

          set({
              currentPointEvents: [...currentPointEvents, newEvent],
              gamePhase: 'PointEnd', pointWinner: winner, lastTouchPlayer: null,
          });
      },

      rollbackToScore: (targetScoreUs: number, targetScoreThem: number): GameEvent[] => {
          const { history, scoreUs, scoreThem } = get();
          if (targetScoreUs > scoreUs || targetScoreThem > scoreThem) return [];
          if (targetScoreUs === scoreUs && targetScoreThem === scoreThem) return [];

          let cutIndex = -1;
          let runningScoreUs = 0;
          let runningScoreThem = 0;
          let lastServingSide: 'us' | 'them' = 'us';

          for (let i = 0; i < history.length; i++) {
              const ev = history[i];
              if (isPhantomEvent(ev)) continue;
              if (isTerminalEvent(ev)) {
                  // Un rallye peut se clôturer sur plusieurs lignes terminales
                  // contiguës (attaque contrée puis bloc point) : seule la dernière
                  // compte, sinon le point serait décompté deux fois.
                  const next = nextRealEvent(history, i);
                  if (next && isTerminalEvent(next) && isSameRally(next, ev)) continue;
                  const pointWinner = computePointWinnerFromEvent(ev);
                  if (pointWinner === 'us') runningScoreUs++; else runningScoreThem++;
                  lastServingSide = pointWinner;
                  if (runningScoreUs === targetScoreUs && runningScoreThem === targetScoreThem) { cutIndex = i + 1; break; }
              }
          }

          if (targetScoreUs === 0 && targetScoreThem === 0) { cutIndex = 0; lastServingSide = 'us'; }
          if (cutIndex === -1) return [];

          const removedEvents = history.slice(cutIndex);
          const keptHistory = history.slice(0, cutIndex);
          const isOpp = get().statTarget === 'them';
          const targetIsServing = isOpp ? (lastServingSide === 'them') : (lastServingSide === 'us');

          set({
              history: keptHistory, currentPointEvents: [],
              scoreUs: targetScoreUs, scoreThem: targetScoreThem,
              servingSide: lastServingSide,
              gamePhase: targetIsServing ? 'Serve' : 'Reception',
              selectedAction: targetIsServing ? 'Service' : 'Réception',
              selectedPlayer: null, lastTouchPlayer: null, pointWinner: null,
          });
          return removedEvents;
      },

      modifyLastPoint: () => {
          const { history, currentPointEvents, scoreUs, scoreThem } = get();
          if (currentPointEvents.length > 0) return;
          if (history.length === 0) return;

          let lastPointEnd = -1;
          for (let i = history.length - 1; i >= 0; i--) {
              const ev = history[i];
              if (isPhantomEvent(ev)) continue;
              if (isTerminalEvent(ev)) { lastPointEnd = i; break; }
          }
          if (lastPointEnd === -1) return;

          // Série de clôture : les lignes terminales contiguës du même rallye
          // (attaque contrée `/` puis bloc point `#`) font partie du point à rejouer.
          let closingRunStart = lastPointEnd;
          for (let i = lastPointEnd - 1; i >= 0; i--) {
              const ev = history[i];
              if (isPhantomEvent(ev)) continue;
              if (!isTerminalEvent(ev) || !isSameRally(ev, history[lastPointEnd])) break;
              closingRunStart = i;
          }

          let pointStartIndex = closingRunStart;
          for (let i = closingRunStart - 1; i >= 0; i--) {
              const ev = history[i];
              // The first point of a set stops at its set start: taken into the point,
              // it made the server read on the previous set's last point (VOL-208).
              if (ev.action === 'SET_START' || ev.action === 'SKIP_TO_SCORE') break;
              if (isPhantomEvent(ev)) { pointStartIndex = i + 1; continue; }
              if (isTerminalEvent(ev)) { pointStartIndex = i + 1; break; }
              pointStartIndex = i;
          }

          const restoredEvents = history.slice(pointStartIndex, lastPointEnd + 1);
          const newHistory = history.slice(0, pointStartIndex);
          const terminalEvent = history[lastPointEnd];
          const lastPointWinner = computePointWinnerFromEvent(terminalEvent);

          const newScoreUs = lastPointWinner === 'us' ? Math.max(0, scoreUs - 1) : scoreUs;
          const newScoreThem = lastPointWinner === 'them' ? Math.max(0, scoreThem - 1) : scoreThem;

          // Qui servait ce point ? On le lisait sur « la 1re action est un Service,
          // donc c'était nous » : juste en solo, faux en mode « équipe adverse »
          // (le Service est le leur) et en deux équipes (tout rallye commence par
          // un Service). On lit désormais l'AUTEUR de la 1re action réelle.
          const firstReal = restoredEvents.find((ev) => !isPhantomEvent(ev));
          const otherOf = (side: 'us' | 'them'): 'us' | 'them' => (side === 'us' ? 'them' : 'us');
          let previousServer: 'us' | 'them' | null = null;
          if (firstReal?.action === 'Service') previousServer = eventAuthorSide(firstReal);
          else if (firstReal?.action === 'Réception') previousServer = otherOf(eventAuthorSide(firstReal));
          if (!previousServer) {
              // Point clos sans service ni réception (point rapide, faute) : le
              // serveur était le vainqueur du point précédent du même set, ou celui
              // qui a engagé le set.
              for (let i = pointStartIndex - 1; i >= 0; i--) {
                  const ev = history[i];
                  if (ev.action === 'SET_START' || ev.action === 'SKIP_TO_SCORE') break;
                  if (isPhantomEvent(ev)) continue;
                  if (isTerminalEvent(ev)) { previousServer = computePointWinnerFromEvent(ev); break; }
              }
          }
          previousServer = previousServer ?? get().setInfos[get().currentSet]?.firstServer ?? lastPointWinner;

          // Le vainqueur n'a tourné que s'il gagnait un side-out. Les deux équipes :
          // seule la nôtre était remise en place, l'adversaire restait tourné.
          if (lastPointWinner !== previousServer) _unrotateTeam(lastPointWinner);
          const newServingSide = previousServer;

          set({
              history: newHistory, currentPointEvents: restoredEvents,
              scoreUs: newScoreUs, scoreThem: newScoreThem, servingSide: newServingSide,
              gamePhase: 'PointEnd', pointWinner: lastPointWinner,
              selectedPlayer: null, lastTouchPlayer: null,
          });
      },

      undo: () => {
         const { currentPointEvents, gamePhase } = get();
         if (gamePhase === 'PointEnd') set({ gamePhase: 'Block', pointWinner: null });
         if (currentPointEvents.length > 0) {
           const nextEvents = currentPointEvents.slice(0, -1);
           set({
             currentPointEvents: nextEvents,
             // Deux équipes : rétablir l'équipe active cohérente avec l'alternance A/B.
             ...(get().dualTeamCapture
               ? { activeTeam: deriveActiveTeam(get().servingSide, toDualRallySteps(nextEvents)) }
               : {}),
           });
         }
      },

      getActivePlayerRoles: () => _active().playerRoles,
      getActiveLibero: () => _active().libero,

      getVisualCourt: (side) => {
        const s = get();
        const target = side ?? s.statTarget;
        return computeVisualCourt({
          team: _team(target),
          statTarget: target,
          servingSide: s.servingSide,
          gamePhase: s.gamePhase,
          isP1SwapActive: p1SwapFor(s, target),
          teamTactics: _tacticsFor(target),
        });
      },

      getSuggestedPlayers: (side) => {
        const s = get();
        const target = side ?? s.statTarget;
        const team = _team(target);
        const tactics = _tacticsFor(target);
        const visualCourt = computeVisualCourt({
          team,
          statTarget: target,
          servingSide: s.servingSide,
          gamePhase: s.gamePhase,
          isP1SwapActive: p1SwapFor(s, target),
          teamTactics: tactics,
        });
        return computeSuggestedPlayers({
          team,
          gamePhase: s.gamePhase,
          lastTouchPlayer: s.lastTouchPlayer,
          visualCourt,
          gameSystem: tactics.gameSystem,
        });
      }

      }; // end return
    }, // end store creator
    {
      name: 'volley-stats-storage',
      version: 6,
      storage: createJSONStorage(() => durableStorage),
      migrate: (persisted: unknown, fromVersion: number) => {
        let state = migrateGameState(persisted, fromVersion);
        if (fromVersion < 2) {
          const court = state.teams.us.court;
          const courtOk =
            court.length >= 6 &&
            court.slice(0, 6).every((n) => typeof n === 'string' && n.trim() !== '');
          state = {
            ...state,
            ownStartingLineupValidated: state.ownStartingLineupValidated ?? courtOk,
            matchSessionTeamId: state.matchSessionTeamId ?? null,
            matchSessionMeta: state.matchSessionMeta ?? null,
          };
        }
        if (fromVersion < 3) {
          const withSets = state as GameState & { setsToWinMatch?: unknown };
          state = {
            ...state,
            setsToWinMatch: clampSetsToWinMatch(withSets.setsToWinMatch),
          };
        }
        if (fromVersion < 4) {
          const meta = state.matchSessionMeta;
          if (meta && typeof meta === 'object') {
            const awayRaw =
              'awayOpponentTeamId' in meta && typeof meta.awayOpponentTeamId === 'string'
                ? meta.awayOpponentTeamId
                : 'opponentTeamId' in meta && typeof meta.opponentTeamId === 'string'
                  ? meta.opponentTeamId
                  : undefined;
            state = {
              ...state,
              matchSessionMeta: {
                opponent: 'opponent' in meta && typeof meta.opponent === 'string' ? meta.opponent : '',
                date: 'date' in meta && typeof meta.date === 'string' ? meta.date : '',
                location: 'location' in meta && typeof meta.location === 'string' ? meta.location : '',
                championship:
                  'championship' in meta && typeof meta.championship === 'string'
                    ? meta.championship
                    : undefined,
                teamId: 'teamId' in meta && typeof meta.teamId === 'string' ? meta.teamId : undefined,
                awayOpponentTeamId: awayRaw,
              },
            };
          }
        }
        if (fromVersion < 6) {
          // Tactiques de l'équipe B : absentes des états persistés antérieurs.
          const withOppTactics = state as GameState & { opponentTeamTactics?: unknown };
          if (!withOppTactics.opponentTeamTactics) {
            state = { ...state, opponentTeamTactics: { ...DEFAULT_TEAM_TACTICS } };
          }
        }
        if (fromVersion < 5) {
          const withKeyboard = state as GameState & { inputSurface?: unknown; captureOptions?: unknown };
          state = {
            ...state,
            inputSurface: withKeyboard.inputSurface === 'keyboard' ? 'keyboard' : 'touch',
            captureOptions: normalizeCaptureOptions(withKeyboard.captureOptions),
          };
        }
        return state;
      },
    }
  )
);

// Its merge replaces the state: a match started while IndexedDB was slow wins.
persistAfterRecovery(useGameStore, { keepChangesOverLateRecovery: true });

// Between sets the next lineup is still being chosen: the previous pair must not move.
const LIBERO_FROZEN_PHASES: ReadonlySet<GamePhase> = new Set<GamePhase>(['SetSetup', 'SetEnd', 'MatchEnd']);

/** Team changes for the automatic reception/defense libero swap (VOL-163), or null. */
export function autoLiberoPatch(state: GameState): Pick<GameState, 'teams'> | null {
  if (LIBERO_FROZEN_PHASES.has(state.gamePhase)) return null;
  const events = [...state.history, ...state.currentPointEvents];
  let teams = state.teams;
  for (const side of ['us', 'them'] as const) {
    const forced = liberoForcedInSet(events, state.currentSet, side);
    const swap = autoLiberoSwap(teams[side], state.servingSide === side, forced);
    if (swap) teams = { ...teams, [side]: { ...teams[side], ...swap } };
  }
  return teams === state.teams ? null : { teams };
}

// One subscriber covers every path that moves the serve (point, undo, set start,
// rollback, restore) instead of patching each of them.
useGameStore.subscribe((state, prev) => {
  if (
    state.servingSide === prev.servingSide && state.teams === prev.teams &&
    state.history === prev.history && state.currentPointEvents === prev.currentPointEvents &&
    state.currentSet === prev.currentSet && state.gamePhase === prev.gamePhase
  ) return;
  const patch = autoLiberoPatch(state);
  if (patch) useGameStore.setState(patch);
});
